import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { isSafeNextPath } from '@mindkeep/shared'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { ApiError } from '@/lib/api-client'
import { acceptInvite, acceptInviteToast, clearInviteToken, fetchPublicInvite } from '@/lib/invite'
import { isOnboardingDone } from '@/lib/onboarding'
import { Button } from '@/components/ui/button'

export function VerifyPage() {
  const [status, setStatus] = useState('Login wird bestätigt...')
  const [params] = useSearchParams()
  const navigate = useNavigate()

  useEffect(() => {
    async function verify() {
      const code = params.get('code')
      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code)
        if (error) {
          setStatus(error.message)
          return
        }
      }

      // M8.2: Einladungs-Token vor dem Session-Check lesen — ohne Session
      // fuehrt der Weg auf die oeffentliche Einladungs-Seite statt in eine Sackgasse.
      const invite = params.get('invite')

      const { data } = await supabase.auth.getSession()
      if (!data.session) {
        if (invite) {
          navigate(`/einladung/${encodeURIComponent(invite)}`, { replace: true })
          return
        }
        setStatus('Kein aktiver Login gefunden. Öffne den Magic-Link erneut.')
        return
      }

      if (invite) {
        try {
          const result = await acceptInvite(invite)
          clearInviteToken()
          toast.success(acceptInviteToast(result.status))
          navigate('/', { replace: true })
        } catch (err) {
          if (err instanceof ApiError && err.status === 404) {
            setStatus('Einladung nicht gefunden oder abgelaufen.')
          } else if (err instanceof ApiError && err.status === 403 && err.code === 'email_mismatch') {
            const publicInvite = await fetchPublicInvite(invite).catch(() => null)
            setStatus(
              publicInvite
                ? `Diese Einladung gehört zu einer anderen E-Mail-Adresse. Melde dich mit ${publicInvite.email} an.`
                : 'Diese Einladung gehört zu einer anderen E-Mail-Adresse.',
            )
          } else {
            setStatus(err instanceof Error ? err.message : 'Einladung konnte nicht angenommen werden.')
          }
        }
        return
      }

      // W11: A kept MCP consent target wins over onboarding/home.
      const next = params.get('next')
      if (isSafeNextPath(next)) {
        navigate(next, { replace: true })
        return
      }

      // M5-A: Nur First-Run-User landen in der Einführung — nach abgeschlossener
      // Einführung (Flag pro User, siehe lib/onboarding.ts) geht es direkt zum Start.
      navigate(isOnboardingDone(data.session.user.id) ? '/' : '/onboarding', { replace: true })
    }

    void verify().catch((err) => {
      setStatus(err instanceof Error ? err.message : 'Verifikation fehlgeschlagen')
    })
  }, [navigate, params])

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-4 text-center">
      <h1 className="text-[26px] font-[650] leading-tight tracking-tight text-foreground">MindKeep</h1>
      <p className="max-w-[400px] text-sm text-muted-foreground" role="status">{status}</p>
      <Button type="button" variant="outline" onClick={() => navigate('/login')}>
        Zum Login
      </Button>
    </main>
  )
}
